import { useEffect, useState } from 'react';

import { PlusMinusNumberInputProps } from './plus-minus-number-input.component';

interface ViewProps {
    isEditing: boolean;
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
    temporaryValue: number;
    onManualInput: (value: string) => void;
    submitManualInput: () => void;
    disabled: {
        decrement: boolean;
        min: boolean;
        plus: boolean;
        increment: boolean;
    };
    onClick: (increment: number) => void;
    increment: number;
}

const usePlusMinusNumberInput = (props: PlusMinusNumberInputProps): ViewProps => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [temporaryValue, setTemporaryValue] = useState<number>(props.value);

    useEffect(() => {
        if (!isEditing) setTemporaryValue(props.value);
    }, [props.value, isEditing]);

    const onClick = (increment: number) => {
        let newValue = props.value + increment;
        if (newValue < (props.min ?? 0)) {
            newValue = props.min ?? 0;
        } else if (newValue > (props.max ?? Infinity)) {
            newValue = props.max ?? Infinity;
        }
        setTemporaryValue(newValue);
        props.onChange(newValue);
    };

    const onManualInput = (value: string) => {
        const numericValue = parseInt(value, 10);
        if (!isNaN(numericValue)) {
            setTemporaryValue(numericValue);
        }
    };

    const submitManualInput = () => {
        setIsEditing(false);
        let actualValue = temporaryValue;
        if (actualValue < (props.min ?? 0)) {
            actualValue = props.min ?? 0;
        } else if (actualValue > (props.max ?? Infinity)) {
            actualValue = props.max ?? Infinity;
        }
        setTemporaryValue(actualValue);
        props.onChange(actualValue);
    };

    return {
        isEditing,
        setIsEditing,
        temporaryValue,
        onManualInput,
        submitManualInput,
        disabled: {
            decrement: props.value - (props.min ?? 0) < (props.increment || 1),
            min: props.value - (props.min ?? 0) < 1,
            plus: (props.max ?? Infinity) - props.value < 1,
            increment: (props.max ?? Infinity) - props.value < (props.increment || 1)
        },
        onClick,
        increment: props.increment || 1
    };
};

export default usePlusMinusNumberInput;
